import type { components } from '../../../generated/schema';
import { apiBaseUrl } from '../api';
import { withCsrfHeader } from '../csrf';

type Schemas = components['schemas'];

type ApiErrorResponse = {
  status: number;
  error?: string;
  message?: string;
  [key: string]: unknown;
};

export type MagicLinkRequestPayload = {
  email: string;
};

export type MagicLinkRequestResponse = Schemas['MagicLinkIssueResponse'];

export type MagicLinkConsumePayload = {
  token: string;
};

export type MagicLinkConsumeResponse = Schemas['MagicLinkConsumeResponse'];
export type AuthMeResponse = Schemas['AuthMeResponse'];
export type LogoutResponse = Schemas['LogoutResponse'];

const apiUrl = (apiBaseUrl || '').replace(/\/+$/, '');

const buildUrl = (path: string) => {
  const normalizedPath = path.replace(/^\/+/, '');
  return `${apiUrl}/${normalizedPath}`;
};

const parseJson = async (res: Response) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

const throwApiError = async (res: Response): Promise<never> => {
  const payload = await parseJson(res);
  const error: ApiErrorResponse = {
    status: res.status,
    ...(payload && typeof payload === 'object' ? payload : {}),
  };
  throw error;
};

export const requestMagicLink = async (
  payload: MagicLinkRequestPayload,
): Promise<MagicLinkRequestResponse> => {
  const res = await fetch(buildUrl('api/auth/magic-link/request'), {
    method: 'POST',
    headers: withCsrfHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    await throwApiError(res);
  }

  return (await parseJson(res)) as MagicLinkRequestResponse;
};

export const consumeMagicLink = async (
  payload: MagicLinkConsumePayload,
): Promise<MagicLinkConsumeResponse> => {
  const res = await fetch(buildUrl('api/auth/magic-link/consume'), {
    method: 'POST',
    headers: withCsrfHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    await throwApiError(res);
  }

  return (await parseJson(res)) as MagicLinkConsumeResponse;
};

export const fetchAuthSession = async (): Promise<AuthMeResponse | null> => {
  const res = await fetch(buildUrl('api/auth/me'), {
    method: 'GET',
    credentials: 'include',
  });

  if (res.status === 401) {
    return null;
  }

  if (!res.ok) {
    await throwApiError(res);
  }

  return (await parseJson(res)) as AuthMeResponse;
};

export const signOut = async (): Promise<LogoutResponse> => {
  const res = await fetch(buildUrl('api/auth/sign-out'), {
    method: 'POST',
    headers: withCsrfHeader(),
    credentials: 'include',
  });

  if (!res.ok) {
    await throwApiError(res);
  }

  return (await parseJson(res)) as LogoutResponse;
};

// ---------------------------------------------------------------------------
// OIDC / Keycloak helpers
// ---------------------------------------------------------------------------

export type OidcConfig = {
  enabled: boolean;
  issuer?: string;
  client_id?: string;
  redirect_uri?: string;
  post_logout_redirect_uri?: string;
  authorization_endpoint?: string;
  token_endpoint?: string;
  end_session_endpoint?: string;
};

export const fetchOidcConfig = async (): Promise<OidcConfig> => {
  const res = await fetch(buildUrl('api/auth/oidc/config'), {
    method: 'GET',
    credentials: 'include',
  });

  if (!res.ok) {
    return { enabled: false };
  }

  return (await parseJson(res)) as OidcConfig;
};

/**
 * Build the URL for the server-assisted OIDC login redirect.
 * The backend /api/auth/oidc/login endpoint will redirect the browser to
 * Keycloak directly.
 */
export const getOidcLoginUrl = (): string => buildUrl('api/auth/oidc/login');

/**
 * Build the URL for server-assisted OIDC logout.
 */
export const getOidcLogoutUrl = (): string => buildUrl('api/auth/oidc/logout');

export type { ApiErrorResponse };
